'use client';

// ПЕРВЫЙ ЭКРАН И ОТЛОЖЕННОЕ «ПОСЛЕ НЕГО» (П162 пп.3–5, владелец 26.09.2026: «хочу, чтобы
// приложение открывалось максимально быстро»). На холодном старте за первые 2,3 с уходило
// ~20 запросов и вызовов моста разом: журнал версий, цифра на иконке, прогрев всех
// разделов стека, экран «Чатов» с шестью ручками. Всё, что человеку не нужно для первого
// экрана, ждёт здесь сигнала «первый экран показан» и простоя.
//
// Сигнал ставят:
//   • родной список «Ботов» — первый удавшийся botsOpen (lib/bots-native.ts);
//   • оболочка — вторым кадром после маунта, если первый экран не родные «Боты» (браузер,
//     другой раздел, старая сборка) — components/cabinet/shell.tsx;
//   • страховка — FALLBACK_MS после первого заказа: сигнал потерялся — отложенное всё
//     равно случится, просто позже.
// Каждое отложенное дело пишет строку журнала `deferred` (имя и время от старта страницы) —
// порядок старта мерится, а не угадывается.
import { log as logJournal } from './client-journal';

/** Страховка: сигнал первого экрана не пришёл за столько мс от первого заказа. */
const FALLBACK_MS = 2500;
/** Потолок ожидания простоя (requestIdleCallback): занятый поток не держит дело вечно. */
const IDLE_TIMEOUT_MS = 1000;

let shownAt: number | null = null;
const waiters = new Set<() => void>();

const nowMs = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** Первый экран показан. Повторные вызовы молчат. */
export function markFirstScreen(src: string): void {
  if (shownAt !== null || typeof window === 'undefined') return;
  shownAt = nowMs();
  logJournal('first.screen', { src });
  const list = [...waiters];
  waiters.clear();
  for (const w of list) w();
}

/** Показан ли уже первый экран. */
export function firstScreenShown(): boolean {
  return shownAt !== null;
}

/** Дождаться простоя главного потока (или потолка). Без requestIdleCallback — таймером. */
function whenIdle(fn: () => void): () => void {
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (typeof w.requestIdleCallback === 'function') {
    const id = w.requestIdleCallback(fn, { timeout: IDLE_TIMEOUT_MS });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(fn, 0);
  return () => window.clearTimeout(id);
}

/**
 * Сделать `fn` через `delayMs` после первого экрана, в простой потока. Возвращает отмену
 * (размонтирование до срока — дело не случится). `name` — строка журнала.
 */
export function afterFirstScreen(name: string, delayMs: number, fn: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  let cancelled = false;
  let cancelStep: () => void = () => undefined;
  const run = (): void => {
    if (cancelled) return;
    logJournal('deferred', { n: name.slice(0, 40), after: shownAt === null ? null : Math.round(nowMs() - shownAt) });
    fn();
  };
  const schedule = (): void => {
    if (cancelled) return;
    const t = window.setTimeout(() => {
      cancelStep = whenIdle(run);
    }, delayMs);
    cancelStep = () => window.clearTimeout(t);
  };
  if (shownAt !== null) {
    schedule();
  } else {
    const fallback = window.setTimeout(() => markFirstScreen('fallback'), FALLBACK_MS);
    const waiter = (): void => {
      window.clearTimeout(fallback);
      schedule();
    };
    waiters.add(waiter);
    cancelStep = () => {
      window.clearTimeout(fallback);
      waiters.delete(waiter);
    };
  }
  return () => {
    cancelled = true;
    cancelStep();
  };
}

/** Только для юнитов: вернуть модуль в состояние «страница только открылась». */
export function resetFirstScreenForTests(): void {
  shownAt = null;
  waiters.clear();
}
